import { useState } from 'react'
import { Link } from 'react-router-dom'
import { X, ExternalLink, AlertTriangle, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
  useSeoFindings,
  useScanFindings,
  useMarkFalsePositive,
  useDeleteSeoScan,
} from '@/hooks/useApi'
import type { ScanRow, SeoFinding, SecurityFinding } from '@/api/client'
import {
  KIND_LABEL,
  STATUS_LABEL,
  STATUS_VARIANT,
  formatDuration,
  scanTypeLabel,
} from '@/lib/scanStatus'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

/**
 * Findings for one scan, opened from the history list.
 *
 * Replaces the two "select a scan → findings" side panels that /seo and
 * /security each had. Both kinds render here; which endpoint is queried
 * follows scan.kind.
 */

const SEVERITY_ORDER = ['critical', 'high', 'warning', 'medium', 'low', 'notice', 'info']

const SEVERITY_STYLE: Record<string, string> = {
  critical: 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300',
  high: 'border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300',
  warning: 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
  medium: 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
  low: 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300',
  notice: 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300',
  info: 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300',
}

function SeverityBadge({ severity }: { severity: string }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded border px-1.5 py-0.5 text-xs font-medium',
        SEVERITY_STYLE[severity] ?? SEVERITY_STYLE.info,
      )}
    >
      {severity}
    </span>
  )
}

function severityRank(s: string): number {
  const i = SEVERITY_ORDER.indexOf(s)
  return i === -1 ? SEVERITY_ORDER.length : i
}

function SeoFindingItem({ finding }: { finding: SeoFinding }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-start gap-2">
        <SeverityBadge severity={finding.severity} />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{finding.name}</p>
          {finding.description && (
            <p className="mt-0.5 text-sm text-muted-foreground">{finding.description}</p>
          )}
          {finding.url && (
            <a
              href={finding.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              <span className="truncate">{finding.url}</span>
            </a>
          )}
          {finding.recommendation && (
            <p className="mt-1.5 rounded bg-muted p-2 text-xs">{finding.recommendation}</p>
          )}
        </div>
      </div>
    </div>
  )
}

function SecurityFindingItem({ finding }: { finding: SecurityFinding }) {
  const markFalsePositive = useMarkFalsePositive()
  return (
    <div className={cn('rounded-lg border p-3', finding.is_false_positive && 'opacity-50')}>
      <div className="flex items-start gap-2">
        <SeverityBadge severity={finding.severity} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium">{finding.name}</p>
            {finding.is_false_positive && (
              <Badge variant="outline" className="text-xs">
                false positive
              </Badge>
            )}
          </div>
          {finding.description && (
            <p className="mt-0.5 text-sm text-muted-foreground">{finding.description}</p>
          )}
          {finding.url && (
            <a
              href={finding.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              <span className="truncate">{finding.url}</span>
            </a>
          )}
          {finding.solution && (
            <p className="mt-1.5 rounded bg-muted p-2 text-xs">{finding.solution}</p>
          )}
          {finding.cwe_id !== null && (
            <p className="mt-1 text-xs text-muted-foreground">CWE-{finding.cwe_id}</p>
          )}
        </div>
        <button
          onClick={() =>
            markFalsePositive.mutate(
              { findingId: finding.id, isFalsePositive: !finding.is_false_positive },
              {
                onSuccess: () =>
                  toast.success(
                    finding.is_false_positive
                      ? 'Weer als echte bevinding gemarkeerd'
                      : 'Gemarkeerd als false positive',
                  ),
                onError: () => toast.error('Markeren mislukt'),
              },
            )
          }
          disabled={markFalsePositive.isPending}
          className="shrink-0 self-start rounded border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
          title="Een false positive telt niet mee in de severity-tellingen"
        >
          {finding.is_false_positive ? 'Terugzetten' : 'False positive'}
        </button>
      </div>
    </div>
  )
}

function FindingsList({ scan, severity }: { scan: ScanRow; severity?: string }) {
  const isSeo = scan.kind === 'seo'
  const seo = useSeoFindings(isSeo ? scan.id : 0, severity ? { severity } : undefined)
  const sec = useScanFindings(isSeo ? 0 : scan.id, severity ? { severity } : undefined)
  const query = isSeo ? seo : sec

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    )
  }

  const findings = query.data ?? []
  if (findings.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground">
        {severity ? 'Geen bevindingen met deze ernst.' : 'Geen bevindingen voor deze scan.'}
      </p>
    )
  }

  const sorted = [...findings].sort((a, b) => severityRank(a.severity) - severityRank(b.severity))

  return (
    <div className="space-y-2">
      {isSeo
        ? (sorted as SeoFinding[]).map((f) => <SeoFindingItem key={f.id} finding={f} />)
        : (sorted as SecurityFinding[]).map((f) => <SecurityFindingItem key={f.id} finding={f} />)}
    </div>
  )
}

export function ScanFindingsDrawer({ scan, onClose }: { scan: ScanRow; onClose: () => void }) {
  const [severity, setSeverity] = useState<string | undefined>()
  const deleteScan = useDeleteSeoScan()
  const severities = scan.kind === 'seo'
    ? ['critical', 'warning', 'notice']
    : ['critical', 'high', 'medium', 'low', 'info']

  // SEO only: there is no delete endpoint for security scans, so offering the
  // button for both kinds would fail on half of them. A running scan is
  // refused by the backend, so it is not offered either — cancel it first.
  const canDelete =
    scan.kind === 'seo' && scan.status !== 'running' && scan.status !== 'pending'

  const handleDelete = () => {
    toast('Scan verwijderen?', {
      description: 'De scan en al zijn bevindingen worden definitief verwijderd.',
      action: {
        label: 'Verwijder',
        onClick: async () => {
          try {
            await deleteScan.mutateAsync(scan.id)
            toast.success('Scan verwijderd')
            onClose()
          } catch (err) {
            toast.error('Verwijderen mislukt', {
              description: err instanceof Error ? err.message : 'Onbekende fout',
            })
          }
        },
      },
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Scrim */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className="relative flex h-full w-full max-w-2xl flex-col border-l bg-background shadow-xl">
        <header className="flex items-start justify-between gap-3 border-b p-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-lg font-semibold">
                {scan.dossier_name ?? scan.target_url}
              </h2>
              <Badge variant="outline">{KIND_LABEL[scan.kind]}</Badge>
              <Badge variant="outline">{scanTypeLabel(scan.scan_type)}</Badge>
              <Badge variant={STATUS_VARIANT[scan.status]}>{STATUS_LABEL[scan.status]}</Badge>
            </div>
            <p className="mt-1 truncate text-xs text-muted-foreground">{scan.target_url}</p>
            <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span>{format(new Date(scan.created_at), 'd MMM yyyy HH:mm')}</span>
              <span>Duur: {formatDuration(scan.duration_seconds)}</span>
              {scan.score !== null && <span>Score: {scan.score}</span>}
              <span>{scan.issue_count} bevinding(en)</span>
            </div>
          </div>
          <button
            onClick={onClose}
            title="Sluiten"
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {scan.status_detail && (
          <div className="flex items-start gap-2 border-b bg-muted/50 p-3 text-sm text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            {scan.status_detail}
          </div>
        )}

        {scan.error_message && (
          <div className="border-b bg-destructive/10 p-3 text-sm text-destructive">
            {scan.error_message}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          <Button
            variant={severity === undefined ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSeverity(undefined)}
          >
            Alles
          </Button>
          {severities.map((s) => (
            <Button
              key={s}
              variant={severity === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSeverity(s)}
            >
              {s}
            </Button>
          ))}
          <div className="ml-auto flex items-center gap-3">
            {scan.dossier_id !== null && (
              <Link
                to={`/dossiers/${scan.dossier_id}`}
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                Naar dossier
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            )}
            {canDelete && (
              <button
                onClick={handleDelete}
                disabled={deleteScan.isPending}
                title="Scan en bevindingen definitief verwijderen"
                className="inline-flex items-center gap-1 rounded border px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Verwijderen
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <FindingsList scan={scan} severity={severity} />
        </div>
      </aside>
    </div>
  )
}
